import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { FileTransfer } from '@capacitor/file-transfer';

/**
 * Interface for tracking download progress
 */
export interface DownloadProgress {
  /** Bytes downloaded so far */
  loaded: number;
  /** Total file size in bytes (0 if unknown) */
  total: number;
  /** Download percentage (0-100) */
  percentage: number;
  /** Download speed in bytes per second */
  speed: number;
  /** Estimated time remaining in seconds */
  estimatedTimeRemaining: number;
  /** Current download phase */
  phase: 'preparing' | 'downloading' | 'saving' | 'installing' | 'complete' | 'error';
  /** Human-readable status message */
  message: string;
}

/**
 * Error types for APK download operations
 */
export enum DownloadErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  INSUFFICIENT_STORAGE = 'INSUFFICIENT_STORAGE',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  FILE_OPERATION_FAILED = 'FILE_OPERATION_FAILED',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Custom error class for download operations
 */
export class DownloadError extends Error {
  constructor(
    public type: DownloadErrorType,
    message: string,
    public originalError?: unknown,
    public timestamp: Date = new Date()
  ) {
    super(message);
    this.name = 'DownloadError';
  }
}

/**
 * Service for downloading and installing APK files
 * Uses @capacitor/file-transfer for efficient large file downloads with progress
 */
@Injectable({
  providedIn: 'root'
})
export class ApkDownloadService {
  // Constants for file management
  private readonly CACHE_DIR = Directory.Cache;
  private readonly APK_SUBDIR = 'updates';

  // Active download tracking
  private isDownloading = false;
  private progressListenerHandle: { remove: () => Promise<void> } | null = null;

  constructor() {
  }

  /**
   * Download and install APK file using FileTransfer plugin
   * @param url URL to download APK from
   * @param filename Name to save the APK file as
   * @param retryCount Current retry attempt (internal use)
   * @returns Observable emitting download progress
   */
  downloadAndInstall(url: string, filename: string, retryCount: number = 0): Observable<DownloadProgress> {
    const progressSubject = new Subject<DownloadProgress>();
    const maxRetries = 3;

    // Prevent multiple simultaneous downloads
    if (this.isDownloading) {
      progressSubject.error(
        new DownloadError(DownloadErrorType.UNKNOWN_ERROR, 'Un download è già in corso. Attendi il completamento.')
      );
      return progressSubject.asObservable();
    }

    this.isDownloading = true;

    // Start the download process
    this.performFileTransferDownload(url, filename, progressSubject)
      .then(() => {
        this.isDownloading = false;
      })
      .catch((error) => {
        this.isDownloading = false;
        const downloadError = this.handleError(error, 'downloading');

        console.error('❌ [ApkDownloadService] Download failure:', downloadError);
        console.error('❌ [ApkDownloadService] Download URL:', url);

        // Implement retry logic for network errors
        if (downloadError.type === DownloadErrorType.NETWORK_ERROR && retryCount < maxRetries) {
          const retryDelay = Math.pow(2, retryCount) * 1000;

          progressSubject.next({
            loaded: 0,
            total: 0,
            percentage: 0,
            speed: 0,
            estimatedTimeRemaining: 0,
            phase: 'preparing',
            message: `Nuovo tentativo tra ${retryDelay / 1000}s...`
          });

          setTimeout(() => {
            this.downloadAndInstall(url, filename, retryCount + 1).subscribe({
              next: (progress) => progressSubject.next(progress),
              error: (err) => progressSubject.error(err),
              complete: () => progressSubject.complete()
            });
          }, retryDelay);
        } else {
          progressSubject.error(downloadError);
        }
      });

    return progressSubject.asObservable();
  }

  /**
   * Cancel ongoing download
   */
  cancelDownload(): void {
    this.isDownloading = false;
    // Remove progress listener if active
    if (this.progressListenerHandle) {
      this.progressListenerHandle.remove();
      this.progressListenerHandle = null;
    }
  }

  /**
   * Perform download using FileTransfer plugin (native download with progress)
   */
  private async performFileTransferDownload(
    url: string,
    filename: string,
    progressSubject: Subject<DownloadProgress>
  ): Promise<void> {
    const startTime = Date.now();
    let lastProgressTime = startTime;
    let lastLoadedBytes = 0;

    try {
      // Phase 1: Preparing
      progressSubject.next({
        loaded: 0,
        total: 0,
        percentage: 0,
        speed: 0,
        estimatedTimeRemaining: 0,
        phase: 'preparing',
        message: 'Preparazione download...'
      });

      // Ensure cache directory exists
      await this.ensureCacheDirectory();

      // Get the full path for the download
      const filepath = `${this.APK_SUBDIR}/${filename}`;

      // Get the URI for the cache directory
      const cacheUri = await Filesystem.getUri({
        path: filepath,
        directory: this.CACHE_DIR
      });

      // Phase 2: Downloading - Set up progress listener
      progressSubject.next({
        loaded: 0,
        total: 0,
        percentage: 0,
        speed: 0,
        estimatedTimeRemaining: 0,
        phase: 'downloading',
        message: 'Connessione al server...'
      });

      // Add progress listener
      this.progressListenerHandle = await FileTransfer.addListener('progress', (event) => {
        const now = Date.now();
        const elapsedSinceLastUpdate = (now - lastProgressTime) / 1000;

        // Calculate speed
        const bytesDownloaded = event.bytes - lastLoadedBytes;
        const speed = elapsedSinceLastUpdate > 0 ? bytesDownloaded / elapsedSinceLastUpdate : 0;

        // Calculate percentage
        const percentage = event.contentLength > 0
          ? Math.round((event.bytes / event.contentLength) * 100)
          : 0;

        // Calculate estimated time remaining
        const remainingBytes = event.contentLength - event.bytes;
        const estimatedTimeRemaining = speed > 0 ? remainingBytes / speed : 0;

        progressSubject.next({
          loaded: event.bytes,
          total: event.contentLength,
          percentage,
          speed,
          estimatedTimeRemaining,
          phase: 'downloading',
          message: `Download: ${percentage}% (${this.formatBytes(event.bytes)} di ${this.formatBytes(event.contentLength)}) - ${this.formatSpeed(speed)}`
        });

        lastProgressTime = now;
        lastLoadedBytes = event.bytes;
      });

      // Perform the download with progress enabled
      const result = await FileTransfer.downloadFile({
        url: url,
        path: cacheUri.uri,
        progress: true,
        readTimeout: 600000, // 10 minutes
        connectTimeout: 60000 // 1 minute
      });

      // Remove progress listener
      if (this.progressListenerHandle) {
        await this.progressListenerHandle.remove();
        this.progressListenerHandle = null;
      }

      // Get file info
      const stat = await Filesystem.stat({
        path: filepath,
        directory: this.CACHE_DIR
      });

      const totalSize = stat.size;
      const elapsedTime = (Date.now() - startTime) / 1000;
      const avgSpeed = elapsedTime > 0 ? totalSize / elapsedTime : 0;

      // Update progress to 100%
      progressSubject.next({
        loaded: totalSize,
        total: totalSize,
        percentage: 100,
        speed: avgSpeed,
        estimatedTimeRemaining: 0,
        phase: 'downloading',
        message: `Download completato: ${this.formatBytes(totalSize)}`
      });

      // Phase 3: Installing
      progressSubject.next({
        loaded: totalSize,
        total: totalSize,
        percentage: 100,
        speed: 0,
        estimatedTimeRemaining: 0,
        phase: 'installing',
        message: 'Avvio installazione...'
      });

      // Open APK for installation
      await this.openApkFile(filepath);

      // Phase 4: Complete
      progressSubject.next({
        loaded: totalSize,
        total: totalSize,
        percentage: 100,
        speed: 0,
        estimatedTimeRemaining: 0,
        phase: 'complete',
        message: 'Installazione avviata'
      });

      progressSubject.complete();
    } catch (error) {
      // Clean up listener on error
      if (this.progressListenerHandle) {
        await this.progressListenerHandle.remove();
        this.progressListenerHandle = null;
      }
      console.error('❌ [ApkDownloadService] Error in performFileTransferDownload:', error);
      throw error;
    }
  }

  /**
   * Ensure cache directory exists
   */
  private async ensureCacheDirectory(): Promise<void> {
    try {
      await Filesystem.mkdir({
        path: this.APK_SUBDIR,
        directory: this.CACHE_DIR,
        recursive: true
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes('exists')) {
        console.warn('⚠️ [ApkDownloadService] Error creating directory:', error);
      }
    }
  }

  /**
   * Format bytes to human-readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Format speed to human-readable string
   */
  private formatSpeed(bytesPerSecond: number): string {
    return this.formatBytes(bytesPerSecond) + '/s';
  }

  /**
   * Handle and classify errors
   */
  private handleError(error: unknown, operation: string): DownloadError {
    const timestamp = new Date();
    console.error(`❌ [ApkDownloadService] Error during ${operation}:`, error);

    const errorMessage = error instanceof Error ? error.message : String(error);

    // Network errors
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('ETIMEDOUT') ||
      errorMessage.includes('ECONNREFUSED') ||
      errorMessage.includes('ENOTFOUND') ||
      errorMessage.includes('Unable to resolve host')
    ) {
      return new DownloadError(
        DownloadErrorType.NETWORK_ERROR,
        'Errore di connessione. Verifica la tua connessione internet e riprova.',
        error,
        timestamp
      );
    }

    // Storage errors
    if (
      errorMessage.includes('storage') ||
      errorMessage.includes('space') ||
      errorMessage.includes('ENOSPC') ||
      errorMessage.includes('quota')
    ) {
      return new DownloadError(
        DownloadErrorType.INSUFFICIENT_STORAGE,
        'Spazio insufficiente. Libera spazio sul dispositivo e riprova.',
        error,
        timestamp
      );
    }

    // Permission errors
    if (errorMessage.includes('permission') || errorMessage.includes('PERMISSION_DENIED')) {
      return new DownloadError(
        DownloadErrorType.PERMISSION_DENIED,
        "Permessi necessari non concessi. Abilita l'installazione da origini sconosciute.",
        error,
        timestamp
      );
    }

    // HTTP errors
    if (errorMessage.includes('HTTP') || errorMessage.includes('status') || errorMessage.includes('404') || errorMessage.includes('500')) {
      return new DownloadError(
        DownloadErrorType.NETWORK_ERROR,
        'Errore durante il download. Il server potrebbe non essere disponibile.',
        error,
        timestamp
      );
    }

    // File operation errors
    if (operation.includes('saving') || operation.includes('opening')) {
      return new DownloadError(
        DownloadErrorType.FILE_OPERATION_FAILED,
        "Errore durante l'operazione sul file. Riprova.",
        error,
        timestamp
      );
    }

    // Unknown errors
    return new DownloadError(DownloadErrorType.UNKNOWN_ERROR, 'Si è verificato un errore imprevisto. Riprova.', error, timestamp);
  }

  /**
   * Open APK file for installation
   */
  async openApkFile(filepath: string): Promise<void> {
    try {
      const result = await Filesystem.getUri({
        path: filepath,
        directory: this.CACHE_DIR
      });

      await FileOpener.open({
        filePath: result.uri,
        contentType: 'application/vnd.android.package-archive'
      });
    } catch (error: unknown) {
      console.error('❌ [ApkDownloadService] Error opening APK:', error);

      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes('permission') || errorMessage.includes('unknown sources')) {
        throw new DownloadError(
          DownloadErrorType.PERMISSION_DENIED,
          "Abilita l'installazione da origini sconosciute nelle impostazioni.",
          error
        );
      }

      // User cancellation is not an error
      if (errorMessage.includes('cancel') || errorMessage.includes('user')) {
        return;
      }

      throw error;
    }
  }

  /**
   * Clean up cached APK files
   */
  async cleanupCachedApk(): Promise<void> {
    try {
      const result = await Filesystem.readdir({
        path: this.APK_SUBDIR,
        directory: this.CACHE_DIR
      });

      const apkFiles = result.files.filter((file: { name?: string } | string) => {
        const fileName = typeof file === 'string' ? file : file.name;
        return fileName?.endsWith('.apk');
      });

      for (const file of apkFiles) {
        const fileName = typeof file === 'string' ? file : (file as { name: string }).name;
        try {
          await Filesystem.deleteFile({
            path: `${this.APK_SUBDIR}/${fileName}`,
            directory: this.CACHE_DIR
          });
        } catch (deleteError) {
          console.error(`❌ [ApkDownloadService] Failed to delete: ${fileName}`, deleteError);
        }
      }

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('does not exist')) {
        return;
      }
      console.error('❌ [ApkDownloadService] Cleanup error:', error);
    }
  }

  /**
   * Check if cached APK exists
   */
  async hasCachedApk(): Promise<boolean> {
    try {
      const result = await Filesystem.readdir({
        path: this.APK_SUBDIR,
        directory: this.CACHE_DIR
      });

      return result.files.some((file: { name?: string } | string) => {
        const fileName = typeof file === 'string' ? file : file.name;
        return fileName?.endsWith('.apk');
      });
    } catch {
      return false;
    }
  }
}
